import { Head, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { companyDocumentsPath, newCompanyDocumentPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface DocumentForm {
  name: string
  document_date: string | null
  notes: string
}

interface Props {
  document: DocumentForm
  errors?: Record<string, string[]>
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Company", href: "/company/resources" },
  { title: "Documents", href: companyDocumentsPath() },
  { title: "New Document", href: newCompanyDocumentPath() },
]

export default function New({ document, errors }: Props) {
  const { data, setData, post, processing, transform } = useForm({
    name: document.name ?? "",
    document_date: document.document_date ?? "",
    notes: document.notes ?? "",
    file: null as File | null,
  })

  transform((data) => ({ company_document: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(companyDocumentsPath(), {
      forceFormData: true,
    })
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Document" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Add Document</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              value={data.document_date ?? ""}
              onChange={(e) => setData("document_date", e.target.value)}
              required
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Brief Description</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              maxLength={50}
              required
              className={inputClass}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">{errors.name[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={5}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Select File</label>
            <input
              type="file"
              onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
              required
              className="text-hpi-med-gray file:bg-hpi-pale-blue file:text-hpi-dark-blue hover:file:bg-hpi-light-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
            />
            {errors?.file && (
              <p className="text-hpi-red mt-1 text-sm">{errors.file[0]}</p>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <a
              href={companyDocumentsPath()}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Uploading..." : "Add Document"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
